import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { isNativeMobile, isAdPrivacyOptionsRequired, showAdPrivacyOptions } from '../utils/adMobService';

const PrivacySecurityScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const content = {
    bn: {
      title: 'গোপনীয়তা এবং নিরাপত্তা',
      dataCollection: 'ডেটা সংগ্রহ',
      dataCollectionText: 'আমরা আপনার শেখার অগ্রগতি, পরীক্ষার ফলাফল এবং অ্যাকাউন্ট তথ্য সংরক্ষণ করি।',
      dataSecurity: 'ডেটা নিরাপত্তা',
      dataSecurityText: 'আপনার ডেটা এনক্রিপ্ট করা এবং নিরাপদ সার্ভারে সংরক্ষিত।',
      thirdParty: 'তৃতীয় পক্ষের শেয়ারিং',
      thirdPartyText: 'আমরা আপনার অনুমতি ছাড়া তৃতীয় পক্ষের সাথে আপনার ডেটা শেয়ার করি না।',
      accountDeletion: 'অ্যাকাউন্ট মুছে ফেলা',
      accountDeletionText: 'আপনি যেকোনো সময় আপনার অ্যাকাউন্ট এবং সমস্ত ডেটা মুছে ফেলতে পারেন।',
      cookies: 'কুকিজ',
      cookiesText: 'আমরা আপনার অভিজ্ঞতা উন্নত করতে কুকিজ ব্যবহার করি।',
      adConsent: 'বিজ্ঞাপন সম্মতি',
      adConsentText: 'ইইএ, যুক্তরাজ্য বা সুইজারল্যান্ডের ব্যবহারকারীরা তাদের ব্যক্তিগতকৃত বিজ্ঞাপন পছন্দ যেকোনো সময় পরিবর্তন করতে পারেন।',
      adConsentButton: 'বিজ্ঞাপন সম্মতি পরিচালনা করুন',
      contact: 'যোগাযোগ করুন',
      contactText: 'গোপনীয়তা সম্পর্কিত প্রশ্নের জন্য, আমাদের সাথে যোগাযোগ করুন।'
    },
    it: {
      title: 'Privacy e Sicurezza',
      dataCollection: 'Raccolta Dati',
      dataCollectionText: 'Raccogliamo i tuoi progressi di apprendimento, risultati dei quiz e informazioni sull\'account.',
      dataSecurity: 'Sicurezza dei Dati',
      dataSecurityText: 'I tuoi dati sono crittografati e archiviati su server sicuri.',
      thirdParty: 'Condivisione con Terze Parti',
      thirdPartyText: 'Non condividiamo i tuoi dati con terze parti senza il tuo consenso.',
      accountDeletion: 'Eliminazione Account',
      accountDeletionText: 'Puoi eliminare il tuo account e tutti i dati in qualsiasi momento.',
      cookies: 'Cookie',
      cookiesText: 'Utilizziamo i cookie per migliorare la tua esperienza.',
      adConsent: 'Consenso Pubblicitario',
      adConsentText: 'Gli utenti nell\'SEE, nel Regno Unito o in Svizzera possono modificare le proprie preferenze sugli annunci personalizzati in qualsiasi momento.',
      adConsentButton: 'Gestisci Consenso Pubblicitario',
      contact: 'Contattaci',
      contactText: 'Per domande sulla privacy, contattaci.'
    },
    en: {
      title: 'Privacy & Security',
      dataCollection: 'Data Collection',
      dataCollectionText: 'We collect your learning progress, quiz results, and account information.',
      dataSecurity: 'Data Security',
      dataSecurityText: 'Your data is encrypted and stored on secure servers.',
      thirdParty: 'Third-Party Sharing',
      thirdPartyText: 'We do not share your data with third parties without your consent.',
      accountDeletion: 'Account Deletion',
      accountDeletionText: 'You can delete your account and all data at any time.',
      cookies: 'Cookies',
      cookiesText: 'We use cookies to improve your experience.',
      adConsent: 'Ad Consent',
      adConsentText: 'Users in the EEA, UK, or Switzerland can change their personalized ad preferences at any time.',
      adConsentButton: 'Manage Ad Consent',
      contact: 'Contact Us',
      contactText: 'For privacy-related questions, please contact us.'
    }
  };

  const text = content[currentLanguage];

  return (
    <div className="privacy-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{text.title}</h1>
        </div>
        <div className="header-btn-placeholder"></div>
      </div>

      <LessonBannerAd />

      <div className="info-content">
        <div className="info-card">
          <div className="info-icon">🔒</div>
          <h3>{text.dataCollection}</h3>
          <p>{text.dataCollectionText}</p>
        </div>

        <div className="info-card">
          <div className="info-icon">🛡️</div>
          <h3>{text.dataSecurity}</h3>
          <p>{text.dataSecurityText}</p>
        </div>

        <div className="info-card">
          <div className="info-icon">🚫</div>
          <h3>{text.thirdParty}</h3>
          <p>{text.thirdPartyText}</p>
        </div>

        <div className="info-card">
          <div className="info-icon">🗑️</div>
          <h3>{text.accountDeletion}</h3>
          <p>{text.accountDeletionText}</p>
        </div>

        <div className="info-card">
          <div className="info-icon">🍪</div>
          <h3>{text.cookies}</h3>
          <p>{text.cookiesText}</p>
        </div>

        {isNativeMobile() && isAdPrivacyOptionsRequired() && (
          <div className="info-card">
            <div className="info-icon">📢</div>
            <h3>{text.adConsent}</h3>
            <p>{text.adConsentText}</p>
            <button
              className="header-btn"
              style={{ width: 'auto', padding: '10px 16px', marginTop: '8px' }}
              onClick={() => showAdPrivacyOptions()}
            >
              {text.adConsentButton}
            </button>
          </div>
        )}

        <div className="info-card">
          <div className="info-icon">📧</div>
          <h3>{text.contact}</h3>
          <p>{text.contactText}</p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default PrivacySecurityScreen;

